import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AutoRefillService } from '../../../services/auto-refill.service';
import { SharedService } from '../../../services/shared.service';
import {
  AUTO_REFILL_FREQ_PRESETS,
  AUTO_REFILL_RX_PAGE_SIZE
} from './auto-refill.constants';
import {
  buildScheduleFreq,
  filterRxList,
  formatDateOnlyForApi,
  formatQty,
  hasScheduleNote,
  isScheduleUrgent,
  pagedItems,
  pickDto,
  scheduleNoteText,
  totalPages
} from './auto-refill.utils';

export interface AutoRefillSelectedPatient {
  patientId: number;
  patientName: string;
  facilityId: number;
  facilityName: string;
}

@Component({
  selector: 'app-auto-refill-patient-profile',
  templateUrl: './auto-refill-patient-profile.component.html',
  styleUrls: ['./auto-refill.component.css']
})
export class AutoRefillPatientProfileComponent implements OnChanges {

  @Input() selectedPharmacyId = 0;
  @Input() patient: AutoRefillSelectedPatient;
  @Output() back = new EventEmitter<void>();
  @Output() checkRow = new EventEmitter<any>();
  @Output() history = new EventEmitter<any>();
  @Output() deactivate = new EventEmitter<any>();
  @Output() editRow = new EventEmitter<any>();
  @Output() error = new EventEmitter<string>();
  @Output() scheduleSaved = new EventEmitter<void>();

  readonly freqPresets = AUTO_REFILL_FREQ_PRESETS;
  readonly rxPageSize = AUTO_REFILL_RX_PAGE_SIZE;

  schedules: any[] = [];
  schedulesLoading = false;
  showRxPanel = true;
  rxList: any[] = [];
  rxLoading = false;
  rxSearchQuery = '';
  rxPage = 1;
  selectedRxIndex = -1;
  freqPreset = 'Q1D (Daily)';
  customDays = 14;
  firstFillDate: Date = new Date();

  constructor(
    private autoRefillService: AutoRefillService,
    private sharedService: SharedService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.patient || changes.selectedPharmacyId) {
      this.resetRxForm();
      this.loadSchedules();
      this.loadRx();
    }
  }

  pick = pickDto;
  formatQty = formatQty;
  isUrgent = isScheduleUrgent;
  hasNote = hasScheduleNote;
  scheduleNote = scheduleNoteText;

  get filteredRxList(): any[] {
    return filterRxList(this.rxList, this.rxSearchQuery);
  }

  get pagedRxList(): any[] {
    return pagedItems(this.filteredRxList, this.rxPage, this.rxPageSize);
  }

  get rxTotalPages(): number {
    return totalPages(this.filteredRxList.length, this.rxPageSize);
  }

  get selectedRxRow(): any {
    if (this.selectedRxIndex < 0) {
      return null;
    }
    var filtered = this.filteredRxList;
    if (!filtered || this.selectedRxIndex >= filtered.length) {
      return null;
    }
    return filtered[this.selectedRxIndex];
  }

  onBack(): void {
    this.back.emit();
  }

  loadSchedules(): void {
    var userId = this.sharedService.getUserId();
    if (!userId || !this.patient || this.selectedPharmacyId <= 0) {
      return;
    }
    var self = this;
    this.schedulesLoading = true;
    this.autoRefillService.getPatientSchedules(
      userId,
      this.selectedPharmacyId,
      this.patient.facilityId,
      this.patient.patientId
    ).subscribe(
      function (data) {
        self.schedules = data || [];
        self.schedulesLoading = false;
      },
      function () {
        self.schedulesLoading = false;
        self.error.emit('Could not load schedules for this patient.');
      }
    );
  }

  loadRx(): void {
    var userId = this.sharedService.getUserId();
    if (!userId || !this.patient || this.selectedPharmacyId <= 0) {
      return;
    }
    var self = this;
    this.rxLoading = true;
    this.autoRefillService.getKrollPatientRx(userId, this.selectedPharmacyId, this.patient.patientId).subscribe(
      function (data) {
        self.rxList = data || [];
        self.rxPage = 1;
        self.rxSearchQuery = '';
        self.selectedRxIndex = -1;
        self.rxLoading = false;
      },
      function () {
        self.rxLoading = false;
        self.error.emit('Could not load prescriptions from Kroll.');
      }
    );
  }

  onRxSearchChange(): void {
    this.rxPage = 1;
    this.selectedRxIndex = -1;
  }

  onRxPageChange(newPage: number): void {
    if (newPage < 1 || newPage > this.rxTotalPages) {
      return;
    }
    this.rxPage = newPage;
  }

  rxGlobalIndex(pageIndex: number): number {
    return (this.rxPage - 1) * this.rxPageSize + pageIndex;
  }

  isRxPageRowSelected(pageIndex: number): boolean {
    return this.selectedRxIndex === this.rxGlobalIndex(pageIndex);
  }

  selectRxPageRow(pageIndex: number): void {
    this.selectedRxIndex = this.rxGlobalIndex(pageIndex);
  }

  onFirstFillInput(value: string): void {
    if (!value || value.length < 10) {
      return;
    }
    var parts = value.split('-');
    if (parts.length !== 3) {
      return;
    }
    var y = parseInt(parts[0], 10);
    var mo = parseInt(parts[1], 10) - 1;
    var day = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(mo) || isNaN(day)) {
      return;
    }
    this.firstFillDate = new Date(y, mo, day);
  }

  saveSchedule(): void {
    var rx = this.selectedRxRow;
    if (!rx || !this.patient) {
      return;
    }
    var userId = this.sharedService.getUserId();
    if (!userId || this.selectedPharmacyId <= 0) {
      return;
    }
    var qtyValue = pickDto(rx, 'qty', 'Qty');
    var qtyParsed = parseFloat(String(qtyValue));
    var body = {
      pharmacyId: this.selectedPharmacyId,
      facilityId: this.patient.facilityId,
      patientId: this.patient.patientId,
      din: pickDto(rx, 'din', 'Din'),
      brandName: pickDto(rx, 'brandName', 'BrandName'),
      generic: pickDto(rx, 'generic', 'Generic'),
      sig: pickDto(rx, 'sig', 'Sig'),
      qty: isNaN(qtyParsed) ? null : qtyParsed,
      scheduleFreq: buildScheduleFreq(this.freqPreset, this.customDays),
      firstFill: formatDateOnlyForApi(this.firstFillDate)
    };
    var self = this;
    this.autoRefillService.saveSchedule(userId, body).subscribe(
      function (res) {
        if (res && (res.success || res.Success)) {
          self.resetRxSelection();
          self.loadSchedules();
          self.scheduleSaved.emit();
        } else {
          self.error.emit((res && res.message) ? res.message : 'Could not save schedule.');
        }
      },
      function () {
        self.error.emit('Could not save schedule.');
      }
    );
  }

  formatDateOnlyForApi = formatDateOnlyForApi;

  private resetRxForm(): void {
    this.freqPreset = 'Q1D (Daily)';
    this.customDays = 14;
    this.firstFillDate = new Date();
    this.resetRxSelection();
  }

  private resetRxSelection(): void {
    this.selectedRxIndex = -1;
    this.rxSearchQuery = '';
    this.rxPage = 1;
  }
}
